import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaArrowLeft, FaChartLine, FaUsers, FaExchangeAlt, FaPercentage, FaUser, FaYoutube, FaInstagram, FaEye, FaThumbsUp, FaComment, FaShare, FaBookmark, FaMoneyBillWave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getBrandsByCompany } from '../../services/companyAuthService';
import { fetchCampaigns } from '../../../Redux/campaignSlice';
import { getCampaignAffiliateFinancials } from '../../../influencer/services/affiliateService';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AffiliateAnalytics = () => {
  const dispatch = useDispatch();
  const { campaigns, loading } = useSelector((state) => state.campaign);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [financialData, setFinancialData] = useState([]);
  const [timeRange, setTimeRange] = useState('7d'); // 7d, 30d, 90d, all
  const [activeTab, setActiveTab] = useState('affiliate-links'); // affiliate-links, youtube, instagram, financial
  const [error, setError] = useState(null);
  const [loadingFinancial, setLoadingFinancial] = useState(false);

  useEffect(() => {
    fetchBrands();
    dispatch(fetchCampaigns());
  }, [dispatch]);

  useEffect(() => {
    // Clear data when selection changes
    setAffiliates([]);
    setFinancialData([]);
    if (selectedCampaign) {
      fetchAffiliateStats();
      fetchFinancialData();
    }
  }, [selectedCampaign, timeRange]);

  // Reset campaign when brand changes
  useEffect(() => {
    setSelectedCampaign('');
    setAffiliates([]);
    setFinancialData([]);
  }, [selectedBrand]);

  const fetchBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await getBrandsByCompany();
      if (response.success) {
        setBrands(response.brands);
      } else {
        toast.error(response.message || 'Failed to fetch brands');
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      toast.error("Failed to fetch brands");
      setError(error.message);
    } finally {
      setLoadingBrands(false);
    }
  };

  const fetchFinancialData = async () => {
    try {
      if (!selectedCampaign) return;

      setLoadingFinancial(true);
      const response = await getCampaignAffiliateFinancials(selectedCampaign);
      
      if (response.success) {
        setFinancialData(response.data || []);
        console.log('Financial data fetched:', response.data);
      } else {
        toast.error(response.message || 'Failed to fetch financial data');
        setFinancialData([]);
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
      toast.error('Failed to fetch financial data');
      setFinancialData([]);
    } finally {
      setLoadingFinancial(false);
    }
  };

  const fetchAffiliateStats = async () => {
    try {
      if (!selectedCampaign) return;

      const influencerStats = {};
      const campaignId = selectedCampaign;

      // Affiliate links
      try {
        const linksResponse = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${campaignId}/affiliate-links`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const linksData = await linksResponse.json();

        if (linksData.success && linksData.data) {
          const affiliateLinks = linksData.data || [];
          affiliateLinks.forEach(link => {
            const userId = link.userId || link.affiliateId || link.affiliate?._id;
            if (!userId) return;

            if (!influencerStats[userId]) {
              influencerStats[userId] = {
                _id: userId,
                userId: userId,
                firstName: link.firstName || link.affiliate?.firstName || 'Unknown',
                lastName: link.lastName || link.affiliate?.lastName || 'User',
                username: link.username || link.affiliate?.username || 'unknown_user',
                email: link.email || link.affiliate?.email || 'unknown@example.com',
                totalVisits: 0,
                uniqueVisits: 0,
                conversions: 0,
                youtubeStats: { totalVideos: 0, approvedVideos: 0, totalViews: 0, totalLikes: 0, totalComments: 0, totalEngagements: 0, videos: [] },
                instagramStats: { totalPosts: 0, approvedPosts: 0, totalLikes: 0, totalComments: 0, totalShares: 0, totalSaves: 0, totalReachs: 0, totalEngagements: 0, posts: [] }
              };
            }

            influencerStats[userId].totalVisits += link.visits || 0;
            influencerStats[userId].uniqueVisits += link.uniqueVisits || 0;
            influencerStats[userId].conversions += link.conversions || 0;
          });
        }
      } catch (e) {
        console.error('Error fetching affiliate links:', e);
      }

      // YouTube media
      try {
        const youtubeResponse = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${campaignId}/youtube-media`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const youtubeData = await youtubeResponse.json();

        if (youtubeData.success && youtubeData.data) {
          youtubeData.data.forEach(influencer => {
            const userId = influencer.userId;
            if (!userId) return;

            if (!influencerStats[userId]) {
              influencerStats[userId] = {
                _id: userId,
                userId: userId,
                firstName: influencer.firstName || 'Unknown',
                lastName: influencer.lastName || 'User',
                username: influencer.username || 'unknown_user',
                email: influencer.email || 'unknown@example.com',
                totalVisits: 0,
                uniqueVisits: 0,
                conversions: 0,
                youtubeStats: { totalVideos: 0, approvedVideos: 0, totalViews: 0, totalLikes: 0, totalComments: 0, totalEngagements: 0, videos: [] },
                instagramStats: { totalPosts: 0, approvedPosts: 0, totalLikes: 0, totalComments: 0, totalShares: 0, totalSaves: 0, totalReachs: 0, totalEngagements: 0, posts: [] }
              };
            }

            influencerStats[userId].youtubeStats = influencer.youtubeStats;
          });
        }
      } catch (e) {
        console.error('Error fetching YouTube data:', e);
      }

      // Instagram media
      try {
        const instagramResponse = await fetch(`${process.env.REACT_APP_API_URL}/company/campaigns/${campaignId}/instagram-media`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const instagramData = await instagramResponse.json();

        if (instagramData.success && instagramData.data) {
          instagramData.data.forEach(influencer => {
            const userId = influencer.userId;
            if (!userId) return;

            if (!influencerStats[userId]) {
              influencerStats[userId] = {
                _id: userId,
                userId: userId,
                firstName: influencer.firstName || 'Unknown',
                lastName: influencer.lastName || 'User',
                username: influencer.username || 'unknown_user',
                email: influencer.email || 'unknown@example.com',
                totalVisits: 0,
                uniqueVisits: 0,
                conversions: 0,
                youtubeStats: { totalVideos: 0, approvedVideos: 0, totalViews: 0, totalLikes: 0, totalComments: 0, totalEngagements: 0, videos: [] },
                instagramStats: { totalPosts: 0, approvedPosts: 0, totalLikes: 0, totalComments: 0, totalShares: 0, totalSaves: 0, totalReachs: 0, totalEngagements: 0, posts: [] }
              };
            }

            influencerStats[userId].instagramStats = influencer.instagramStats;
          });
        }
      } catch (e) {
        console.error('Error fetching Instagram data:', e);
      }

      const aggregatedAffiliates = Object.values(influencerStats).sort((a, b) => (b.conversions || 0) - (a.conversions || 0));
      setAffiliates(aggregatedAffiliates);
    } catch (err) {
      console.error('Error aggregating affiliate stats:', err);
      toast.error(err.message || 'Failed to fetch affiliate analytics');
      setAffiliates([]);
    }
  };

  // Calculate overall statistics
  const calculateOverallStats = () => {
    let totalAffiliates = affiliates.length;
    let totalVisits = affiliates.reduce((sum, aff) => sum + (aff.totalVisits || 0), 0);
    let totalConversions = affiliates.reduce((sum, aff) => sum + (aff.conversions || 0), 0);
    const totalYouTubeViews = affiliates.reduce((sum, aff) => sum + (aff.youtubeStats?.totalViews || 0), 0);
    const totalYouTubeEngagements = affiliates.reduce((sum, aff) => sum + (aff.youtubeStats?.totalEngagements || 0), 0);
    const totalInstagramReachs = affiliates.reduce((sum, aff) => sum + (aff.instagramStats?.totalReachs || 0), 0);
    const totalInstagramEngagements = affiliates.reduce((sum, aff) => sum + (aff.instagramStats?.totalEngagements || 0), 0);

    // Financial statistics
    const totalDue = financialData.reduce((sum, aff) => sum + (aff.financial?.totalDue || 0), 0);
    const totalPaid = financialData.reduce((sum, aff) => sum + (aff.financial?.amountPaid || 0), 0);
    const totalUnpaid = financialData.reduce((sum, aff) => sum + (aff.financial?.amountUnpaid || 0), 0);

    const avgConversionRate = totalVisits > 0
      ? ((totalConversions / totalVisits) * 100).toFixed(2)
      : '0.00';

    return {
      totalAffiliates,
      totalVisits,
      totalConversions,
      avgConversionRate,
      totalYouTubeViews,
      totalYouTubeEngagements,
      totalInstagramReachs,
      totalInstagramEngagements,
      totalDue,
      totalPaid,
      totalUnpaid
    };
  };

  if (loading || loadingBrands) {
    return (
      <div className="flex justify-center items-center min-h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-full">
        <div className="text-red-500 text-center">
          <h2 className="text-xl font-bold mb-2">Error Loading Data</h2>
          <p>{error}</p>
          <button
            onClick={() => {
              setError(null);
              fetchBrands();
              dispatch(fetchCampaigns());
            }}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const overallStats = calculateOverallStats();

  return (
    <div className=" min-h-full mt-6">
      <div className="container mx-auto p-4 md:p-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div className="flex items-center mb-4 md:mb-0">
            <button
              onClick={() => window.history.back()}
              className="text-gray-600 p-2 rounded-full hover:bg-gray-200 lg:mr-4"
            >
              <FaArrowLeft size={22} />
            </button>
            <h2 className="lg:text-2xl text-xl font-bold text-gray-800">
              Affiliate Analytics
            </h2>
          </div>

          {/* Time Range Selector */}
          <div className="flex space-x-2">
            {['7d', '30d', '90d', 'all'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 rounded-full text-sm ${timeRange === range
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
              >
                {range === 'all' ? 'All Time' : range}
              </button>
            ))}
          </div>
        </div>

        {/* Brand & Campaign Selection */}
        <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
          <div className="mb-4">
            <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-2">
              Select Brand
            </label>
            <select
              id="brand"
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Select a brand</option>
              {brands.map((brand) => (
                <option key={brand._id} value={brand._id}>
                  {brand.brandName}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-2">
            <label htmlFor="campaign" className="block text-sm font-medium text-gray-700 mb-2">
              Select Campaign
            </label>
            <select
              id="campaign"
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
              disabled={!selectedBrand}
            >
              <option value="">{selectedBrand ? 'Select a campaign' : 'Select a brand first'}</option>
              {campaigns
                .filter(c => (c.brandId?._id || c.brandId) === selectedBrand)
                .map(c => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100 text-orange-500">
                <FaUsers size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Affiliates</p>
                <p className="text-2xl font-semibold">{overallStats.totalAffiliates}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-500">
                <FaChartLine size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Visits</p>
                <p className="text-2xl font-semibold">{overallStats.totalVisits}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-500">
                <FaExchangeAlt size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Conversions</p>
                <p className="text-2xl font-semibold">{overallStats.totalConversions}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 text-purple-500">
                <FaPercentage size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Avg. Conversion Rate</p>
                <p className="text-2xl font-semibold">{overallStats.avgConversionRate}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100 text-red-500">
                <FaYoutube size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">YouTube Views</p>
                <p className="text-2xl font-semibold">{overallStats.totalYouTubeViews?.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100 text-red-500">
                <FaThumbsUp size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">YouTube Engagements</p>
                <p className="text-2xl font-semibold">{overallStats.totalYouTubeEngagements?.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-pink-100 text-pink-500">
                <FaInstagram size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Instagram Reachs</p>
                <p className="text-2xl font-semibold">{overallStats.totalInstagramReachs?.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-pink-100 text-pink-500">
                <FaThumbsUp size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Instagram Engagements</p>
                <p className="text-2xl font-semibold">{overallStats.totalInstagramEngagements?.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-500">
                <FaMoneyBillWave size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Due</p>
                <p className="text-2xl font-semibold">
                  {financialData.length > 0 && financialData[0].financial?.currency?.symbol 
                    ? `${financialData[0].financial.currency.symbol}${overallStats.totalDue?.toLocaleString() || '0'}`
                    : `$${overallStats.totalDue?.toLocaleString() || '0'}`
                  }
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-500">
                <FaMoneyBillWave size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-2xl font-semibold">
                  {financialData.length > 0 && financialData[0].financial?.currency?.symbol 
                    ? `${financialData[0].financial.currency.symbol}${overallStats.totalPaid?.toLocaleString() || '0'}`
                    : `$${overallStats.totalPaid?.toLocaleString() || '0'}`
                  }
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100 text-red-500">
                <FaMoneyBillWave size={24} />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-500">Total Unpaid</p>
                <p className="text-2xl font-semibold">
                  {financialData.length > 0 && financialData[0].financial?.currency?.symbol 
                    ? `${financialData[0].financial.currency.symbol}${overallStats.totalUnpaid?.toLocaleString() || '0'}`
                    : `$${overallStats.totalUnpaid?.toLocaleString() || '0'}`
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav 
              className="flex overflow-x-auto px-4 sm:px-6" 
              style={{
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                WebkitScrollbar: { display: 'none' }
              }} 
              aria-label="Tabs"
            >
              <div className="flex space-x-4 sm:space-x-8 min-w-max">
                <button
                  onClick={() => setActiveTab('affiliate-links')}
                  className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${activeTab === 'affiliate-links'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <FaChartLine className="inline mr-1 sm:mr-2 text-xs sm:text-sm" />
                  <span className="hidden sm:inline">Affiliate Links</span>
                  <span className="sm:hidden">Links</span>
                </button>
                <button
                  onClick={() => setActiveTab('youtube')}
                  className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${activeTab === 'youtube'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <FaYoutube className="inline mr-1 sm:mr-2 text-xs sm:text-sm" />
                  <span className="hidden sm:inline">YouTube Media</span>
                  <span className="sm:hidden">YouTube</span>
                </button>
                <button
                  onClick={() => setActiveTab('instagram')}
                  className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${activeTab === 'instagram'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <FaInstagram className="inline mr-1 sm:mr-2 text-xs sm:text-sm" />
                  <span className="hidden sm:inline">Instagram Media</span>
                  <span className="sm:hidden">Instagram</span>
                </button>
                <button
                  onClick={() => setActiveTab('financial')}
                  className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center ${activeTab === 'financial'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <FaMoneyBillWave className="inline mr-1 sm:mr-2 text-xs sm:text-sm" />
                  <span className="hidden sm:inline">Financial</span>
                  <span className="sm:hidden">Finance</span>
                </button>
              </div>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            {activeTab === 'affiliate-links' && (
              <>
                <h4 className="text-lg font-semibold mb-4">Affiliate Link Performance</h4>
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Affiliate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Visits
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unique Visits
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Conversions
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Conversion Rate
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {affiliates.map((affiliate) => (
                        <tr key={affiliate._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                  <FaUser className="text-orange-500" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {(affiliate.firstName && affiliate.firstName !== 'Unknown' &&
                                    affiliate.lastName && affiliate.lastName !== 'User')
                                    ? `${affiliate.firstName} ${affiliate.lastName}`
                                    : affiliate.username || 'Unknown User'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {affiliate.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{affiliate.totalVisits || 0}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{affiliate.uniqueVisits || 0}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{affiliate.conversions || 0}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {affiliate.totalVisits > 0
                                ? ((affiliate.conversions / affiliate.totalVisits) * 100).toFixed(2)
                                : '0.00'}%
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'youtube' && (
              <>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <FaYoutube className="text-red-500 mr-2" />
                  YouTube Media Performance
                </h4>
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Influencer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Videos
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaEye className="inline mr-1" />
                          Views
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaThumbsUp className="inline mr-1" />
                          Likes
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaComment className="inline mr-1" />
                          Comments
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Engagements
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {affiliates.map((affiliate) => (
                        <tr key={affiliate._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                                  <FaUser className="text-red-500" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {(affiliate.firstName && affiliate.firstName !== 'Unknown' &&
                                    affiliate.lastName && affiliate.lastName !== 'User')
                                    ? `${affiliate.firstName} ${affiliate.lastName}`
                                    : affiliate.username || 'Unknown User'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {affiliate.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {affiliate.youtubeStats?.totalVideos || 0}
                              <span className="text-xs text-gray-500 ml-1">
                                ({affiliate.youtubeStats?.approvedVideos || 0} approved)
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.youtubeStats?.totalViews || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.youtubeStats?.totalLikes || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.youtubeStats?.totalComments || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.youtubeStats?.totalEngagements || 0).toLocaleString()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'instagram' && (
              <>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <FaInstagram className="text-pink-500 mr-2" />
                  Instagram Media Performance
                </h4>
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Influencer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Posts
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaEye className="inline mr-1" />
                         Reachs
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaThumbsUp className="inline mr-1" />
                          Likes
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaComment className="inline mr-1" />
                          Comments
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaShare className="inline mr-1" />
                          Shares
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <FaBookmark className="inline mr-1" />
                          Saves
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Engagements
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {affiliates.map((affiliate) => (
                        <tr key={affiliate._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center">
                                  <FaUser className="text-pink-500" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {(affiliate.firstName && affiliate.firstName !== 'Unknown' &&
                                    affiliate.lastName && affiliate.lastName !== 'User')
                                    ? `${affiliate.firstName} ${affiliate.lastName}`
                                    : affiliate.username || 'Unknown User'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {affiliate.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {affiliate.instagramStats?.totalPosts || 0}
                              <span className="text-xs text-gray-500 ml-1">
                                ({affiliate.instagramStats?.approvedPosts || 0} approved)
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalReachs || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalLikes || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalComments || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalShares || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalSaves || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(affiliate.instagramStats?.totalEngagements || 0).toLocaleString()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {activeTab === 'financial' && (
              <>
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <FaMoneyBillWave className="text-orange-500 mr-2" />
                  Financial Performance
                </h4>
                {console.log('Financial tab active, data:', financialData, 'loading:', loadingFinancial)}
                {loadingFinancial ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                    <span className="ml-2 text-gray-600">Loading financial data...</span>
                  </div>
                ) : financialData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FaMoneyBillWave className="mx-auto text-4xl mb-4 text-gray-300" />
                    <p>No financial data available for this campaign</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Influencer
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Due
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount Paid
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount Unpaid
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Currency
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Updated
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {financialData.map((affiliate) => {
                          try {
                            return (
                          <tr key={affiliate._id} className="hover:bg-gray-50">
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                                  <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                    <FaUser className="text-orange-500 text-xs sm:text-sm" />
                                  </div>
                                </div>
                                <div className="ml-2 sm:ml-4 min-w-0">
                                  <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                                    {(affiliate.firstName && affiliate.firstName !== 'Unknown' &&
                                      affiliate.lastName && affiliate.lastName !== 'User')
                                      ? `${affiliate.firstName} ${affiliate.lastName}`
                                      : affiliate.username || 'Unknown User'}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">
                                    {affiliate.email}
                                  </div>
                                  {affiliate.instagramId && affiliate.instagramId !== 'N/A' && (
                                    <div className="text-xs text-gray-400 truncate">
                                      @{affiliate.instagramId}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm font-medium text-gray-900">
                                {affiliate.financial?.currency?.symbol || '$'}{(affiliate.financial?.totalDue || 0).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-green-600 font-medium">
                                {affiliate.financial?.currency?.symbol || '$'}{(affiliate.financial?.amountPaid || 0).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-red-600 font-medium">
                                {affiliate.financial?.currency?.symbol || '$'}{(affiliate.financial?.amountUnpaid || 0).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-900">
                                {typeof affiliate.financial?.currency === 'object' 
                                  ? affiliate.financial?.currency?.code || 'USD'
                                  : affiliate.financial?.currency || 'USD'
                                }
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-xs sm:text-sm text-gray-500">
                                {affiliate.financial?.lastUpdated 
                                  ? new Date(affiliate.financial.lastUpdated).toLocaleDateString()
                                  : 'N/A'
                                }
                              </div>
                            </td>
                          </tr>
                            );
                          } catch (error) {
                            console.error('Error rendering affiliate row:', error, affiliate);
                            return null;
                          }
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateAnalytics; 